if (!customElements.get('lusso-header')) {
  customElements.define(
    'lusso-header',
    class LussoHeader extends HTMLElement {
      connectedCallback() {
        this.navItems = [...this.querySelectorAll('[data-nav-item]')];
        this.mobileNav = this.querySelector('[data-mobile-nav]');
        this.mobileSummary = this.mobileNav?.querySelector(':scope > summary');
        this.mobilePanel = this.mobileNav?.querySelector('.ls-mobile-nav__panel');
        this.mobileClose = this.querySelector('[data-mobile-close]');
        this.desktopMedia = window.matchMedia('(min-width: 1200px)');
        this.lastScrollY = window.scrollY;
        this.scrollTicking = false;
        this.openDelay = null;
        this.closeDelay = null;

        this.handleDocumentClick = (event) => {
          if (!this.contains(event.target)) this.closeDesktopMenus();
        };
        this.handleKeydown = (event) => this.onKeydown(event);
        this.handleScroll = () => this.onScroll();
        this.handleResize = () => this.onResize();
        this.handleMobileToggle = () => this.onMobileToggle();
        this.handleMobileSummaryClick = (event) => {
          // Use an explicit toggle below desktop so the custom drawer remains
          // reliable on touch browsers even when native details events are
          // delayed or suppressed by an overlapping hero layer.
          if (this.isDesktopLayout()) return;
          event.preventDefault();
          this.mobileNav.open = !this.mobileNav.open;
          this.onMobileToggle();
        };
        this.handleMobileClose = () => this.closeMobileNav(true);
        this.handleEditorUnload = (event) => {
          if (event.detail?.sectionId === this.dataset.sectionId) this.cleanup();
        };

        this.navItems.forEach((item) => this.bindNavItem(item));
        this.mobileNav?.addEventListener('toggle', this.handleMobileToggle);
        this.mobileSummary?.addEventListener('click', this.handleMobileSummaryClick);
        this.mobileClose?.addEventListener('click', this.handleMobileClose);
        document.addEventListener('click', this.handleDocumentClick);
        document.addEventListener('keydown', this.handleKeydown);
        window.addEventListener('scroll', this.handleScroll, { passive: true });
        window.addEventListener('resize', this.handleResize, { passive: true });
        document.addEventListener('shopify:section:unload', this.handleEditorUnload);

        this.resizeObserver = new ResizeObserver(() => this.setHeaderOffset());
        this.resizeObserver.observe(this);
        this.updateNavigationFit();
        this.setHeaderOffset();
        this.updateScrollState();
        document.fonts?.ready.then(() => this.updateNavigationFit());
      }

      disconnectedCallback() {
        this.cleanup();
      }

      cleanup() {
        window.clearTimeout(this.openDelay);
        window.clearTimeout(this.closeDelay);
        this.resizeObserver?.disconnect();
        this.mobileNav?.removeEventListener('toggle', this.handleMobileToggle);
        this.mobileClose?.removeEventListener('click', this.handleMobileClose);
        document.removeEventListener('click', this.handleDocumentClick);
        document.removeEventListener('keydown', this.handleKeydown);
        window.removeEventListener('scroll', this.handleScroll);
        window.removeEventListener('resize', this.handleResize);
        document.removeEventListener('shopify:section:unload', this.handleEditorUnload);
        this.mobileSummary?.removeEventListener('click', this.handleMobileSummaryClick);
        this.navItems?.forEach((item) => this.unbindNavItem(item));
        document.body.classList.remove('ls-nav-open');
      }

      bindNavItem(item) {
        const trigger = item.querySelector('[data-nav-trigger]');
        if (!trigger) return;

        item._lussoHandlers = {
          click: () => this.toggleDesktopMenu(item),
          enter: () => {
            window.clearTimeout(this.closeDelay);
            this.openDelay = window.setTimeout(() => this.openDesktopMenu(item), 90);
          },
          leave: () => {
            window.clearTimeout(this.openDelay);
            this.closeDelay = window.setTimeout(() => this.closeDesktopMenu(item), 150);
          },
          focusin: () => this.openDesktopMenu(item),
          focusout: (event) => {
            if (!item.contains(event.relatedTarget)) this.closeDesktopMenu(item);
          },
        };

        trigger.addEventListener('click', item._lussoHandlers.click);
        item.addEventListener('pointerenter', item._lussoHandlers.enter);
        item.addEventListener('pointerleave', item._lussoHandlers.leave);
        item.addEventListener('focusin', item._lussoHandlers.focusin);
        item.addEventListener('focusout', item._lussoHandlers.focusout);
      }

      unbindNavItem(item) {
        const trigger = item.querySelector('[data-nav-trigger]');
        const handlers = item._lussoHandlers;
        if (!trigger || !handlers) return;
        trigger.removeEventListener('click', handlers.click);
        item.removeEventListener('pointerenter', handlers.enter);
        item.removeEventListener('pointerleave', handlers.leave);
        item.removeEventListener('focusin', handlers.focusin);
        item.removeEventListener('focusout', handlers.focusout);
        delete item._lussoHandlers;
      }

      toggleDesktopMenu(item) {
        if (item.classList.contains('is-open')) {
          this.closeDesktopMenu(item);
        } else {
          this.openDesktopMenu(item);
        }
      }

      openDesktopMenu(item) {
        if (!this.isDesktopLayout()) return;
        this.closeDesktopMenus(item);
        item.classList.add('is-open');
        item.querySelector('[data-nav-trigger]')?.setAttribute('aria-expanded', 'true');
        this.classList.add('has-open-menu');
        this.classList.remove('is-hidden');
      }

      closeDesktopMenu(item, restoreFocus = false) {
        if (!item) return;
        const trigger = item.querySelector('[data-nav-trigger]');
        item.classList.remove('is-open');
        trigger?.setAttribute('aria-expanded', 'false');
        if (restoreFocus) trigger?.focus();
        if (!this.querySelector('[data-nav-item].is-open')) this.classList.remove('has-open-menu');
      }

      closeDesktopMenus(except = null) {
        this.navItems.forEach((item) => {
          if (item !== except) this.closeDesktopMenu(item);
        });
      }

      onMobileToggle() {
        const open = Boolean(this.mobileNav?.open);
        document.body.classList.toggle('ls-nav-open', open);
        this.mobileSummary?.setAttribute('aria-label', open ? 'Close navigation' : 'Open navigation');
        this.mobileSummary?.setAttribute('aria-expanded', String(open));
        this.classList.toggle('has-open-menu', open);
        this.classList.remove('is-hidden');

        if (open) {
          this.updateMobilePanelOffset();
          window.requestAnimationFrame(() => {
            this.mobilePanel?.querySelector('a, button, summary')?.focus();
          });
        } else {
          this.mobilePanel?.style.removeProperty('--ls-mobile-nav-offset');
        }
      }

      updateMobilePanelOffset() {
        if (!this.mobilePanel) return;
        const ticker = document.querySelector('.ls-ticker-section');
        const tickerBottom = ticker ? ticker.getBoundingClientRect().bottom : 0;
        const visibleTickerHeight = Math.max(0, Math.min(window.innerHeight, tickerBottom));
        this.mobilePanel.style.setProperty('--ls-mobile-nav-offset', `${Math.round(visibleTickerHeight)}px`);
      }

      closeMobileNav(restoreFocus = false) {
        if (!this.mobileNav?.open) return;
        this.mobileNav.open = false;
        if (restoreFocus) this.mobileSummary?.focus();
      }

      onKeydown(event) {
        if (event.key === 'Escape') {
          const openItem = this.querySelector('[data-nav-item].is-open');
          if (openItem) {
            event.preventDefault();
            this.closeDesktopMenu(openItem, true);
          } else if (this.mobileNav?.open) {
            event.preventDefault();
            this.closeMobileNav(true);
          }
          return;
        }

        if (event.key !== 'Tab' || !this.mobileNav?.open || this.isDesktopLayout()) return;
        const focusable = [...this.mobilePanel.querySelectorAll('a[href], button:not([disabled]), summary, [tabindex]:not([tabindex="-1"])')].filter(
          (element) => !element.closest('[hidden]') && element.getClientRects().length > 0,
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      onScroll() {
        if (this.scrollTicking) return;
        this.scrollTicking = true;
        window.requestAnimationFrame(() => {
          this.updateScrollState();
          this.scrollTicking = false;
        });
      }

      updateScrollState() {
        const currentY = Math.max(window.scrollY, 0);
        const scrollingDown = currentY > this.lastScrollY;
        const menuOpen = this.classList.contains('has-open-menu') || Boolean(this.querySelector('details[open]'));
        this.classList.toggle('is-scrolled', currentY > 8);

        if (this.dataset.sticky === 'on-scroll-up') {
          const shouldHide = scrollingDown && currentY > this.offsetHeight * 1.5 && !menuOpen;
          this.classList.toggle('is-hidden', shouldHide);
        } else {
          this.classList.remove('is-hidden');
        }

        this.lastScrollY = currentY;
      }

      onResize() {
        this.updateNavigationFit();
        this.setHeaderOffset();
        if (this.mobileNav?.open) this.updateMobilePanelOffset();
        if (this.isDesktopLayout()) this.closeMobileNav(false);
        else this.closeDesktopMenus();
      }

      isDesktopLayout() {
        return this.desktopMedia.matches && !this.classList.contains('is-nav-condensed');
      }

      updateNavigationFit() {
        if (!this.desktopMedia.matches) {
          this.classList.add('is-nav-condensed');
          this.classList.remove('is-nav-compact', 'is-nav-force-expanded');
          return;
        }

        // Keep the desktop composition stable at desktop widths. The drawer
        // remains the intentional layout below 1200px; desktop should not
        // unexpectedly collapse because a long navigation label is measured
        // one frame too early or a preview cookie restores an old class.
        this.classList.remove('is-nav-condensed', 'is-nav-compact');
        this.classList.add('is-nav-force-expanded');
      }

      setHeaderOffset() {
        const height = Math.round(this.getBoundingClientRect().height);
        if (height > 0) this.style.setProperty('--ls-header-height', `${height}px`);
      }
    },
  );
}
